import { Present } from "@root/constants";
import { ChapterBox, OptionRow } from "./style";
import { Controller } from "react-hook-form";
import { Box, Checkbox, Collapse, Stack, Text } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";

// Updated interface to include isSelected property
interface VideoWithSelection {
  id: string;
  name: string;
  isSelected: boolean;
}

interface ChapterWithVideosSelection {
  id: string;
  name: string;
  videos: VideoWithSelection[];
}

export function CourseAccessOptionsList({
  options,
  control,
  register,
  watch,
  chapters,
  selectedChaptersObj,
  selectedVideosObj,
  handleChapterChange,
  handleVideoChange,
  toggleChapterExpansion,
  expandedChapters,
  isChapterIndeterminate,
  showChapterDropdown,
}: {
  options: CourseAccessOption[];
  control: any;
  register: any;
  watch: any;
  chapters: ChapterWithVideosSelection[];
  selectedChaptersObj: Record<string, boolean>;
  selectedVideosObj: Record<string, boolean>;
  handleChapterChange: (chapterId: string, checked: boolean) => void;
  handleVideoChange: (
    videoId: string,
    chapterId: string,
    checked: boolean
  ) => void;
  toggleChapterExpansion: (chapterId: string) => void;

  expandedChapters: Record<string, boolean>;
  isChapterIndeterminate: (chapterId: string) => boolean;
  showChapterDropdown: boolean;
}) {
  const { t } = useTranslation("common");
  console.log("chapters", chapters);
  if (!Array.isArray(options) || options?.length === Present.none) {
    return null;
  }
  return (
    <>
      {options?.map((option) => {
        const isParentChecked = watch(option?.name);
        return (
          <OptionRow
            key={option?.name}
            flexDirection={"column"}
            padding={"10px 15px"}
            sx={{
              ...(isParentChecked ? { backgroundColor: "#F4F4F4" } : {}),
            }}
          >
            <Controller
              name={option?.name as string}
              control={control}
              render={({ field }) => (
                <Checkbox
                  isChecked={!!field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  spacing="16px"
                >
                  <Box>
                    <Text fontSize={"16px"} fontWeight={500}>
                      {t(option?.labelKey)}
                    </Text>
                  </Box>
                </Checkbox>
              )}
            />
            <Collapse
              in={
                option?.name === "freeAccessEnabled"
                  ? showChapterDropdown
                  : !!isParentChecked
              }
              animateOpacity
            >
              {option?.name === "freeAccessEnabled" && (
                <ChapterBox>
                  {/* Render the PublicAccessSection content when "Free Access" is enabled */}
                  <ChapterCheckboxList
                    chapters={chapters}
                    register={register}
                    selectedChaptersObj={selectedChaptersObj}
                    selectedVideosObj={selectedVideosObj}
                    handleChapterChange={handleChapterChange}
                    handleVideoChange={handleVideoChange}
                    toggleChapterExpansion={toggleChapterExpansion}
                    expandedChapters={expandedChapters}
                    isChapterIndeterminate={isChapterIndeterminate}
                  />
                </ChapterBox>
              )}
            </Collapse>
          </OptionRow>
        );
      })}
    </>
  );
}

const ChapterCheckboxList = ({
  chapters,
  selectedChaptersObj,
  selectedVideosObj,
  handleChapterChange,
  handleVideoChange,
  toggleChapterExpansion,
  expandedChapters,
  isChapterIndeterminate,
}: {
  chapters: ChapterWithVideosSelection[];
  register: any;
  selectedChaptersObj: Record<string, boolean>;
  selectedVideosObj: Record<string, boolean>;
  handleChapterChange: (chapterId: string, checked: boolean) => void;
  handleVideoChange: (
    videoId: string,
    chapterId: string,
    checked: boolean
  ) => void;
  toggleChapterExpansion: (chapterId: string) => void;
  expandedChapters: Record<string, boolean>;
  isChapterIndeterminate: (chapterId: string) => boolean;
}) => {
  if (!Array.isArray(chapters) || chapters?.length === Present.none) {
    return null;
  }
  return (
    <Stack spacing={2}>
      {chapters?.map((chapter) => (
        <Box key={chapter.id}>
          {/* Chapter checkbox with expand/collapse icon */}
          <OptionRow>
            <Stack
              direction="row"
              align="center"
              justifyContent={"space-between"}
              spacing={2}
              flex={1}
            >
              <Checkbox
                isChecked={selectedChaptersObj[chapter.id] || false}
                isIndeterminate={isChapterIndeterminate(chapter.id)}
                onChange={(e) =>
                  handleChapterChange(chapter.id, e.target.checked)
                }
                spacing="16px"
                colorScheme="blue"
              >
                <Text fontSize="16px" fontWeight={500}>
                  {chapter?.name}
                </Text>
              </Checkbox>
              {chapter?.videos?.length && (
                <Box
                  cursor="pointer"
                  onClick={() => toggleChapterExpansion(chapter.id)}
                  display="flex"
                  alignItems="center"
                >
                  {expandedChapters[chapter.id] ? (
                    <ChevronDownIcon w={4} h={4} />
                  ) : (
                    <ChevronRightIcon w={4} h={4} />
                  )}
                </Box>
              )}
            </Stack>
          </OptionRow>

          {/* Videos section */}
          <Collapse in={expandedChapters[chapter.id]} animateOpacity>
            <Box ml={8} mt={2}>
              {chapter.videos?.map((video) => (
                <OptionRow key={video.id} mb={2}>
                  <Checkbox
                    isChecked={selectedVideosObj[video.id] || false}
                    onChange={(e) =>
                      handleVideoChange(video.id, chapter.id, e.target.checked)
                    }
                    spacing="16px"
                    colorScheme="blue"
                  >
                    <Text fontSize="14px" fontWeight={400}>
                      {video.name}
                    </Text>
                  </Checkbox>
                </OptionRow>
              ))}
            </Box>
          </Collapse>
        </Box>
      ))}
    </Stack>
  );
};
